<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">
          {{ pageTitle }}
          <button type="button" class="help-icon-btn" :class="{ active: showHelp }" @click="showHelp = !showHelp" title="查看設定說明">
            <i class="ri-question-line"></i>
          </button>
        </h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleReset">重置</button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting" class="spinner spinner-sm"></span>
          <span v-else>儲存設定</span>
        </button>
      </div>
    </div>

    <div class="page-container">

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else class="card">
      <div class="card-body">

        <div v-if="activeRuleLoading" class="time-range-active-loading">
          <span class="spinner spinner-sm"></span>
          檢查時間區間給點規則中...
        </div>

        <div v-else-if="activeTimeRangeRule" class="time-range-overrides-banner">
          <div class="overrides-banner-header">
            <div class="overrides-title-row">
              <span class="overrides-pulse-dot"></span>
              <h2 class="overrides-title">時間區間給點規則目前生效中</h2>
              <span :class="getTimeRangeStatusBadgeClass(activeTimeRangeRule.status)">
                {{ getTimeRangeStatusText(activeTimeRangeRule.status) }}
              </span>
            </div>
            <div class="overrides-actions">
              <router-link
                :to="`/client/points/earning-rules/time-range/${activeTimeRangeRule.id}/edit`"
                class="btn btn-sm btn-secondary"
              >
                <i class="ri-edit-line"></i>
                編輯時間區間規則
              </router-link>
              <router-link
                to="/client/points/earning-rules/time-range"
                class="btn btn-sm btn-outline-warning"
              >
                查看全部規則
              </router-link>
            </div>
          </div>

          <p class="overrides-lead">
            活動期間內的新訂單將<strong>優先套用時間區間給點規則</strong>，本頁的常態設定暫不生效。活動結束後將自動恢復常態給點規則。
          </p>

          <div class="overrides-rule-card">
            <div class="overrides-rule-name">{{ activeTimeRangeRule.ruleName }}</div>
            <div class="overrides-rule-period">
              <i class="ri-time-line"></i>
              {{ formatDateTime(activeTimeRangeRule.startTime) }}
              <span class="period-sep">～</span>
              {{ formatDateTime(activeTimeRangeRule.endTime) }}
            </div>

            <div class="overrides-details">
              <div class="overrides-detail-block">
                <div class="overrides-detail-label">目前套用的會員等級回饋率</div>
                <div class="tier-rate-list">
                  <span
                    v-for="(rate, tier) in activeTimeRangeRule.tierConfig"
                    :key="tier"
                    class="tier-rate-badge"
                  >
                    {{ getTierName(tier) }}: {{ rate }}%
                  </span>
                  <span v-if="!hasActiveTierRates(activeTimeRangeRule.tierConfig)" class="overrides-detail-empty">
                    沿用本頁常態回饋率
                  </span>
                </div>
              </div>

              <div class="overrides-detail-block">
                <div class="overrides-detail-label">目前套用的給點計算設定</div>
                <div v-if="activeTimeRangeRule.useNormalCalculationSettings" class="overrides-calc-summary">
                  <span class="calc-mode-tag calc-mode-tag--normal">沿用本頁常態給點規則</span>
                  <span>金額基數：每 {{ form.baseAmount }} 元</span>
                  <span>捨入方式：{{ roundingMethodLabel }}</span>
                  <span>生效天數：{{ form.effectiveDays || 0 }} 天</span>
                </div>
                <div v-else class="overrides-calc-summary">
                  <span class="calc-mode-tag calc-mode-tag--custom">時間區間自訂</span>
                  <span>金額基數：每 {{ activeTimeRangeRule.baseAmount || 1 }} 元</span>
                  <span>捨入方式：{{ getRoundingMethodLabel(activeTimeRangeRule.roundingMethod) }}</span>
                  <span>生效天數：{{ activeTimeRangeRule.effectiveDays || 0 }} 天</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 啟用狀態 -->
        <div class="rule-status-bar" :class="ruleStatus === 'active' ? 'status-active' : 'status-inactive'">
          <div class="status-left">
            <span class="status-dot"></span>
            <span class="status-label">
              {{ ruleStatus === 'active' ? '規則已啟用：所有會員消費均可累積點數' : '規則已停用：消費不累積點數' }}
            </span>
          </div>
          <div class="status-right">
            <button
              v-if="ruleStatus !== 'active'"
              type="button"
              class="btn btn-success btn-sm"
              :disabled="toggling || !rule"
              :title="!rule ? '請先完成設定並儲存後，才能啟用規則' : ''"
              @click="handleEnable"
            >
              <span v-if="toggling" class="spinner spinner-sm"></span>
              <i v-else class="ri-checkbox-circle-line"></i>
              {{ toggling ? '處理中...' : '啟用規則' }}
            </button>
            <button
              v-else
              type="button"
              class="btn btn-warning btn-sm"
              :disabled="toggling"
              @click="handleDisable"
            >
              <span v-if="toggling" class="spinner spinner-sm"></span>
              <i v-else class="ri-forbid-line"></i>
              {{ toggling ? '處理中...' : '停用規則' }}
            </button>
          </div>
        </div>

        <!-- 使用說明（點擊 ? 展開） -->
        <transition name="guide-fade">
          <div v-if="showHelp" class="setup-guide-alert">
            <i class="ri-information-line"></i>
            <div>
              <strong>常態給點規則設定說明</strong>
              <ol>
                <li>在「會員等級回饋率」頁籤設定各等級的點數回饋率（%）。</li>
                <li>在「給點規則與記錄」頁籤設定計算基數、捨入方式與生效天數。</li>
                <li>完成設定後點擊「<strong>儲存設定</strong>」，規則建立成功後「啟用規則」按鈕才會解除鎖定。</li>
                <li>點擊「<strong>啟用規則</strong>」後，會員消費即開始依回饋率累積點數。</li>
              </ol>
            </div>
            <button type="button" class="guide-close-btn" @click="showHelp = false">
              <i class="ri-close-line"></i>
            </button>
          </div>
        </transition>

        <!-- 頁籤列 -->
        <div class="tab-bar">
          <button
            type="button"
            class="tab-btn"
            :class="{ active: activeTab === 'tiers' }"
            @click="activeTab = 'tiers'"
          >
            <i class="ri-medal-line"></i>
            會員等級回饋率
          </button>
          <button
            type="button"
            class="tab-btn"
            :class="{ active: activeTab === 'settings' }"
            @click="activeTab = 'settings'"
          >
            <i class="ri-settings-3-line"></i>
            給點規則與記錄
          </button>
        </div>

        <form @submit.prevent="handleSubmit">

          <!-- 頁籤一：會員等級回饋率 -->
          <div v-show="activeTab === 'tiers'">
            <div class="quick-rule-summary">
              <div class="quick-rule-summary-title">
                <i class="ri-lightbulb-line"></i>
                目前給點規則
              </div>
              <div class="quick-rule-summary-items">
                <span>金額基數：每 {{ form.baseAmount }} 元</span>
                <span>捨入方式：{{ roundingMethodLabel }}</span>
                <span>生效天數：{{ form.effectiveDays || 0 }} 天</span>
                <span
                  class="effective-mode-badge"
                  :class="(form.effectiveDays || 0) > 0 ? 'effective-mode-delayed' : 'effective-mode-immediate'"
                >
                  {{ (form.effectiveDays || 0) > 0 ? '延後生效模式' : '立即生效' }}
                </span>
              </div>
              <button type="button" class="btn btn-sm btn-secondary" @click="activeTab = 'settings'">
                前往「給點規則與記錄」
              </button>
            </div>

            <div class="form-section">
              <div v-if="tiers.length === 0" class="alert alert-warning">
                <p>尚未設定會員等級，請先前往 <router-link to="/client/member-tiers">會員等級管理</router-link> 建立等級</p>
              </div>

              <div v-else class="tiers-layout">
                <!-- 左側：回饋率設定 -->
                <div class="tiers-left">
                  <h3 class="sub-section-title">回饋率設定</h3>
                  <div class="tier-config-list">
                    <div v-for="tier in tiers" :key="tier.id" class="tier-config-item">
                      <div class="tier-info">
                        <span v-if="tier.color" class="tier-name" :style="{ color: tier.color }">
                          {{ tier.tierName }}
                        </span>
                        <span v-else class="tier-name">{{ tier.tierName }}</span>
                        <code class="tier-code">{{ tier.tierCode }}</code>
                      </div>
                      <div class="tier-input">
                        <div class="input-group">
                          <input
                            v-model.number="form.tierConfig[tier.tierCode]"
                            type="number"
                            class="form-control"
                            :class="{ 'is-invalid': errors[`tier_${tier.tierCode}`] }"
                            placeholder="0"
                            min="0"
                            step="0.1"
                          />
                          <span class="input-group-text">%</span>
                        </div>
                        <div v-if="errors[`tier_${tier.tierCode}`]" class="invalid-feedback">
                          {{ errors[`tier_${tier.tierCode}`] }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 右側：計算預覽 -->
                <div class="tiers-right">
                  <h3 class="sub-section-title">計算預覽</h3>
                  <div class="preview-amount-row">
                    <label class="preview-amount-label">輸入消費金額</label>
                    <div class="input-group preview-amount-input">
                      <span class="input-group-text">NT$</span>
                      <input
                        v-model.number="previewAmount"
                        type="number"
                        class="form-control"
                        min="0"
                        step="1"
                        placeholder="100"
                      />
                    </div>
                    <small class="preview-settings-hint">
                      基數 {{ form.baseAmount }} 元・{{ roundingMethodLabel }}
                    </small>
                  </div>

                  <div class="preview-table-wrap">
                    <table class="preview-table">
                      <thead>
                        <tr>
                          <th>等級</th>
                          <th>回饋率</th>
                          <th>單位數</th>
                          <th>點數</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="tier in tiers" :key="tier.id">
                          <td>
                            <span v-if="tier.color" :style="{ color: tier.color }" class="preview-tier-name">{{ tier.tierName }}</span>
                            <span v-else class="preview-tier-name">{{ tier.tierName }}</span>
                          </td>
                          <td>{{ form.tierConfig[tier.tierCode] || 0 }}%</td>
                          <td class="preview-units">{{ tierPreview(tier.tierCode).units }}</td>
                          <td class="preview-points">
                            <strong>{{ tierPreview(tier.tierCode).points }}</strong> 點
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div class="preview-formula-note">
                    <i class="ri-information-line"></i>
                    第一段：{{ previewAmount || 0 }} ÷ {{ form.baseAmount }} = {{ previewRawUnits }}，{{ roundingMethodLabel }}得 <strong>{{ previewUnits }}</strong> 單位<br/>
                    第二段：單位數 × 各等級回饋率，{{ roundingMethodLabel }}得點數
                  </div>
                </div>
              </div>
            </div>

            <div class="form-section">
              <h2 class="section-title">變更備註（選填）</h2>
              <div class="form-group">
                <label class="form-label">備註說明</label>
                <textarea
                  v-model="form.remark"
                  class="form-control"
                  rows="3"
                  placeholder="請輸入此次變更的備註說明（選填）"
                  maxlength="500"
                ></textarea>
                <small class="form-text">此備註將記錄在變更歷史中，方便日後追蹤變更原因（最多 500 字）</small>
              </div>
            </div>

            <div v-if="rule && rule.updatedAt" class="form-section">
              <h2 class="section-title">變更記錄</h2>
              <div class="info-row">
                <span class="info-label">最後更新時間：</span>
                <span class="info-value">{{ formatDateTime(rule.updatedAt) }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">最後更新人員：</span>
                <span class="info-value">{{ rule.updatedByName || '系統' }}</span>
              </div>
              <div class="info-row">
                <button type="button" class="btn btn-sm btn-info" @click="showLogs = !showLogs">
                  <i class="ri-file-list-line"></i>
                  {{ showLogs ? '隱藏' : '查看' }}變更歷史
                </button>
              </div>
            </div>

            <div v-if="showLogs && rule" class="form-section">
              <h2 class="section-title">變更歷史</h2>

              <div v-if="logsLoading" class="loading-state">
                <div class="spinner spinner-sm"></div>
                <p>載入中...</p>
              </div>

              <div v-else-if="logsError" class="error-state">
                <p class="error-message">{{ logsError }}</p>
                <button class="btn btn-sm btn-secondary" @click="fetchLogs">重試</button>
              </div>

              <div v-else class="logs-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>操作時間</th>
                      <th>操作類型</th>
                      <th>操作人員</th>
                      <th>變更內容</th>
                      <th>備註</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="log in logs" :key="log.id">
                      <td>{{ formatDateTime(log.changedAt) }}</td>
                      <td>
                        <span :class="getActionBadgeClass(log.action)">
                          {{ getActionText(log.action) }}
                        </span>
                      </td>
                      <td>{{ getChangedByDisplay(log) }}</td>
                      <td>
                        <div class="change-details">
                          <div v-if="log.oldValue" class="old-value">
                            <strong>變更前：</strong>
                            <pre>{{ formatChangeValue(log.oldValue) }}</pre>
                          </div>
                          <div v-if="log.newValue" class="new-value">
                            <strong>變更後：</strong>
                            <pre>{{ formatChangeValue(log.newValue) }}</pre>
                          </div>
                          <div v-if="!log.oldValue && !log.newValue" class="no-change">
                            -
                          </div>
                        </div>
                      </td>
                      <td>{{ log.remark || '-' }}</td>
                    </tr>
                    <tr v-if="logs.length === 0">
                      <td colspan="5" class="empty-state">
                        尚無變更記錄
                      </td>
                    </tr>
                  </tbody>
                </table>

                <ListPagination
                  v-if="logsPagination.totalPages > 1"
                  :page="logsPagination.page"
                  :total-pages="logsPagination.totalPages"
                  :total="logsPagination.totalItems"
                  @change="handleLogsPageChange"
                />
              </div>
            </div>

          </div>

          <!-- 頁籤二：給點規則與記錄 -->
          <div v-show="activeTab === 'settings'">
            <div class="form-section">
              <h2 class="section-title">給點規則設定</h2>

              <div class="calc-preview-box">
                <div class="calc-preview-title"><i class="ri-calculator-line"></i> 計算公式預覽</div>
                <div class="calc-preview-steps">
                  <div class="calc-step">
                    <span class="calc-step-num">1</span>
                    <span class="calc-step-text">
                      消費金額 ÷ <strong>{{ form.baseAmount }}</strong> = 單位數
                      <span class="calc-arrow">→</span>
                      依捨入方式取整
                    </span>
                  </div>
                  <div class="calc-step">
                    <span class="calc-step-num">2</span>
                    <span class="calc-step-text">
                      單位數 × 各等級回饋率 = 點數
                      <span class="calc-arrow">→</span>
                      再次依捨入方式取整
                    </span>
                  </div>
                </div>
                <div class="calc-example">
                  例：消費 180 元，基數 {{ calcPreview.baseAmount }}，回饋率 2.5，{{ roundingMethodLabel }}<br/>
                  → 第一段 180 ÷ {{ calcPreview.baseAmount }} = {{ calcPreview.rawUnits }} → <strong>{{ calcPreview.units }} 單位</strong><br/>
                  → 第二段 {{ calcPreview.units }} × 2.5 = {{ calcPreview.rawPoints }} → <strong>{{ calcPreview.points }} 點</strong>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">金額計算基數（元）</label>
                <div class="base-amount-row">
                  <div class="base-amount-presets">
                    <button
                      v-for="preset in [1, 10, 50, 100]"
                      :key="preset"
                      type="button"
                      class="base-amount-btn"
                      :class="{ active: form.baseAmount === preset }"
                      @click="form.baseAmount = preset"
                    >
                      每 {{ preset }} 元
                    </button>
                  </div>
                  <div class="base-amount-custom">
                    <input
                      v-model.number="form.baseAmount"
                      type="number"
                      class="form-control"
                      min="1"
                      step="1"
                      placeholder="自訂"
                    />
                    <span class="input-suffix">元</span>
                  </div>
                </div>
                <small class="form-text">消費金額除以此基數後，再套用捨入方式得到單位數</small>
              </div>

              <div class="form-group">
                <label class="form-label">計算基準</label>
                <select v-model="form.calculationBase" class="form-control" disabled>
                  <option value="paid_amount">實付金額（扣除優惠券、折扣後）</option>
                </select>
                <small class="form-text">目前僅支援實付金額計算</small>
              </div>

              <div class="form-group">
                <label class="form-label">捨入方式（兩段均適用）</label>
                <select v-model="form.roundingMethod" class="form-control">
                  <option value="round">四捨五入</option>
                  <option value="ceil">無條件進位</option>
                  <option value="floor">無條件捨去</option>
                </select>
                <small class="form-text">第一段（金額÷基數）與第二段（單位×回饋率）均使用此方式取整</small>
              </div>

              <div class="form-group">
                <label class="form-label">生效天數</label>
                <input
                  v-model.number="form.effectiveDays"
                  type="number"
                  class="form-control"
                  :class="{ 'is-invalid': errors.effectiveDays }"
                  placeholder="0"
                  min="0"
                />
                <div v-if="errors.effectiveDays" class="invalid-feedback">
                  {{ errors.effectiveDays }}
                </div>
                <small class="form-text">0 = 立即生效，N = 購買後 N 天生效</small>
              </div>
            </div>

            <div class="form-section">
              <h2 class="section-title">變更備註（選填）</h2>
              <div class="form-group">
                <label class="form-label">備註說明</label>
                <textarea
                  v-model="form.remark"
                  class="form-control"
                  :class="{ 'is-invalid': errors.remark }"
                  rows="3"
                  placeholder="請輸入此次變更的備註說明（選填）"
                  maxlength="500"
                ></textarea>
                <div v-if="errors.remark" class="invalid-feedback">
                  {{ errors.remark }}
                </div>
                <small class="form-text">此備註將記錄在變更歷史中，方便日後追蹤變更原因（最多 500 字）</small>
              </div>
            </div>

            <div v-if="rule && rule.updatedAt" class="form-section">
              <h2 class="section-title">變更記錄</h2>
              <div class="info-row">
                <span class="info-label">最後更新時間：</span>
                <span class="info-value">{{ formatDateTime(rule.updatedAt) }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">最後更新人員：</span>
                <span class="info-value">{{ rule.updatedByName || '系統' }}</span>
              </div>
              <div class="info-row">
                <button type="button" class="btn btn-sm btn-info" @click="showLogs = !showLogs">
                  <i class="ri-file-list-line"></i>
                  {{ showLogs ? '隱藏' : '查看' }}變更歷史
                </button>
              </div>
            </div>

            <!-- 變更記錄列表 -->
            <div v-if="showLogs && rule" class="form-section">
              <h2 class="section-title">變更歷史</h2>

              <div v-if="logsLoading" class="loading-state">
                <div class="spinner spinner-sm"></div>
                <p>載入中...</p>
              </div>

              <div v-else-if="logsError" class="error-state">
                <p class="error-message">{{ logsError }}</p>
                <button class="btn btn-sm btn-secondary" @click="fetchLogs">重試</button>
              </div>

              <div v-else class="logs-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>操作時間</th>
                      <th>操作類型</th>
                      <th>操作人員</th>
                      <th>變更內容</th>
                      <th>備註</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="log in logs" :key="log.id">
                      <td>{{ formatDateTime(log.changedAt) }}</td>
                      <td>
                        <span :class="getActionBadgeClass(log.action)">
                          {{ getActionText(log.action) }}
                        </span>
                      </td>
                      <td>{{ getChangedByDisplay(log) }}</td>
                      <td>
                        <div class="change-details">
                          <div v-if="log.oldValue" class="old-value">
                            <strong>變更前：</strong>
                            <pre>{{ formatChangeValue(log.oldValue) }}</pre>
                          </div>
                          <div v-if="log.newValue" class="new-value">
                            <strong>變更後：</strong>
                            <pre>{{ formatChangeValue(log.newValue) }}</pre>
                          </div>
                          <div v-if="!log.oldValue && !log.newValue" class="no-change">
                            -
                          </div>
                        </div>
                      </td>
                      <td>{{ log.remark || '-' }}</td>
                    </tr>
                    <tr v-if="logs.length === 0">
                      <td colspan="5" class="empty-state">
                        尚無變更記錄
                      </td>
                    </tr>
                  </tbody>
                </table>

                <ListPagination
                  v-if="logsPagination.totalPages > 1"
                  :page="logsPagination.page"
                  :total-pages="logsPagination.totalPages"
                  :total="logsPagination.totalItems"
                  @change="handleLogsPageChange"
                />
              </div>
            </div>

          </div>

        </form>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, reactive, watch, computed } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { pointsEarningRulesService } from '@/api/modules/pointsEarningRules.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'PointsEarningRulesNormal',
  setup() {
    const { pageTitle } = usePageTitle('常態給點規則設定');
    const activeTab = ref('tiers');
    const showHelp = ref(false);
    const loading = ref(false);
    const submitting = ref(false);
    const toggling = ref(false);
    const error = ref(null);
    const rule = ref(null);
    const ruleStatus = ref('draft');
    const tiers = ref([]);
    const showLogs = ref(false);
    const logs = ref([]);
    const logsLoading = ref(false);
    const logsError = ref(null);
    const logsPagination = ref({
      page: 1,
      pageSize: 20,
      totalItems: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false
    });
    const activeRule = ref(null);
    const activeRuleLoading = ref(false);

    const activeTimeRangeRule = computed(() => {
      if (!activeRule.value || activeRule.value.ruleType !== 'time_range') {
        return null;
      }
      return activeRule.value;
    });

    const form = reactive({
      tierConfig: {},
      calculationBase: 'paid_amount',
      roundingMethod: 'floor',
      baseAmount: 1,
      effectiveDays: 0,
      remark: ''
    });

    const errors = reactive({});

    const fetchActiveRule = async () => {
      activeRuleLoading.value = true;
      try {
        const response = await pointsEarningRulesService.getActiveRule();
        if (response && response.success) {
          activeRule.value = response.data || null;
        }
      } catch (err) {
        console.error('獲取生效規則失敗:', err);
      } finally {
        activeRuleLoading.value = false;
      }
    };

    const fetchTiers = async () => {
      try {
        const response = await memberTierService.getTiers({ isActive: true });
        if (response && response.success && response.data) {
          tiers.value = response.data;
          
          // 初始化 tierConfig
          if (!rule.value || !rule.value.tierConfig) {
            tiers.value.forEach(tier => {
              form.tierConfig[tier.tierCode] = tier.pointsRewardRate || 0;
            });
          }
        }
      } catch (err) {
        console.error('獲取會員等級列表失敗:', err);
      }
    };

    const fetchRule = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await pointsEarningRulesService.getNormalRule();
        
        if (response && response.success) {
          if (response.data) {
            rule.value = response.data;
            ruleStatus.value = response.data.status || 'draft';
            form.tierConfig = { ...response.data.tierConfig };
            form.calculationBase = response.data.calculationBase || 'paid_amount';
            form.roundingMethod = response.data.roundingMethod || 'floor';
            form.baseAmount = response.data.baseAmount || 1;
            form.effectiveDays = response.data.effectiveDays || 0;
          } else {
            // 如果沒有規則，使用會員等級的預設回饋率
            await fetchTiers();
            tiers.value.forEach(tier => {
              form.tierConfig[tier.tierCode] = tier.pointsRewardRate || 0;
            });
          }
        } else {
          error.value = response?.message || '獲取規則失敗';
        }
      } catch (err) {
        console.error('獲取規則失敗:', err);
        error.value = err.message || '獲取規則失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {
      // 清除錯誤
      Object.keys(errors).forEach(key => delete errors[key]);

      // 驗證
      let hasError = false;
      
      // 檢查是否至少設定一個會員等級
      const hasTierConfig = Object.values(form.tierConfig).some(rate => rate > 0);
      if (!hasTierConfig) {
        errors.tierConfig = '至少需設定一個會員等級的回饋率';
        hasError = true;
      }

      // 驗證回饋率範圍
      for (const [tierCode, rate] of Object.entries(form.tierConfig)) {
        if (rate < 0) {
          errors[`tier_${tierCode}`] = '回饋率不可為負數';
          hasError = true;
        }
      }

      if (hasError) {
        return;
      }

      submitting.value = true;

      try {
        const response = await pointsEarningRulesService.updateNormalRule({
          tierConfig: form.tierConfig,
          calculationBase: form.calculationBase,
          roundingMethod: form.roundingMethod,
          baseAmount: form.baseAmount || 1,
          effectiveDays: form.effectiveDays,
          remark: form.remark || undefined
        });

        if (response && response.success) {
          await showSuccess('設定儲存成功', '常態給點規則已更新');
          form.remark = ''; // 清空備註欄位
          await fetchRule();
        } else {
          throw new Error(response?.message || '儲存失敗');
        }
      } catch (err) {
        console.error('儲存規則失敗:', err);
        await showError('儲存失敗', err.message || '儲存規則時發生錯誤');
      } finally {
        submitting.value = false;
      }
    };

    const handleEnable = async () => {
      toggling.value = true;
      try {
        const response = await pointsEarningRulesService.enableNormalRule();
        if (response && response.success) {
          ruleStatus.value = 'active';
          rule.value = response.data;
          await showSuccess('已啟用', '常態給點規則已啟用，消費可累積點數');
        }
      } catch (err) {
        await showError('啟用失敗', err.response?.data?.message || err.message);
      } finally {
        toggling.value = false;
      }
    };

    const handleDisable = async () => {
      toggling.value = true;
      try {
        const response = await pointsEarningRulesService.disableNormalRule();
        if (response && response.success) {
          ruleStatus.value = 'disabled';
          rule.value = response.data;
          await showSuccess('已停用', '常態給點規則已停用');
        }
      } catch (err) {
        await showError('停用失敗', err.response?.data?.message || err.message);
      } finally {
        toggling.value = false;
      }
    };

    const handleReset = () => {
      if (rule.value) {
        form.tierConfig = { ...rule.value.tierConfig };
        form.calculationBase = rule.value.calculationBase || 'paid_amount';
        form.roundingMethod = rule.value.roundingMethod || 'floor';
        form.baseAmount = rule.value.baseAmount || 1;
        form.effectiveDays = rule.value.effectiveDays || 0;
        form.remark = '';
      } else {
        tiers.value.forEach(tier => {
          form.tierConfig[tier.tierCode] = tier.pointsRewardRate || 0;
        });
        form.calculationBase = 'paid_amount';
        form.roundingMethod = 'floor';
        form.baseAmount = 1;
        form.effectiveDays = 0;
        form.remark = '';
      }
      Object.keys(errors).forEach(key => delete errors[key]);
    };

    const formatDateTime = (dateTime) => {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const fetchLogs = async () => {
      if (!rule.value || !rule.value.id) {
        logs.value = [];
        return;
      }

      logsLoading.value = true;
      logsError.value = null;

      try {
        const params = {
          page: logsPagination.value.page,
          pageSize: logsPagination.value.pageSize
        };

        const response = await pointsEarningRulesService.getNormalRuleLogs(params);

        if (response && response.success && response.data) {
          logs.value = response.data.items || [];
          logsPagination.value = response.data.pagination || logsPagination.value;
        } else {
          logsError.value = response?.message || '獲取變更記錄失敗';
        }
      } catch (err) {
        console.error('獲取變更記錄失敗:', err);
        logsError.value = err.message || '獲取變更記錄失敗';
      } finally {
        logsLoading.value = false;
      }
    };

    const handleLogsPageChange = (page) => {
      logsPagination.value.page = page;
      fetchLogs();
    };

    const getActionBadgeClass = (action) => {
      const classes = {
        create: 'badge badge-success',
        update: 'badge badge-info',
        enable: 'badge badge-success',
        disable: 'badge badge-warning',
        delete: 'badge badge-danger'
      };
      return classes[action] || 'badge badge-secondary';
    };

    const getActionText = (action) => {
      const texts = {
        create: '建立',
        update: '更新',
        enable: '啟用',
        disable: '停用',
        delete: '刪除'
      };
      return texts[action] || action;
    };

    const formatChangeValue = (value) => {
      if (!value) return '-';
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value);
        } catch (e) {
          return value;
        }
      }
      return JSON.stringify(value, null, 2);
    };

    const getChangedByDisplay = (log) => {
      if (!log.changedBy) return '系統';
      // 優先顯示 name，如果沒有則顯示 username，都沒有則顯示 ID
      if (log.changedByName) return log.changedByName;
      if (log.changedByUsername) return log.changedByUsername;
      return log.changedBy;
    };

    const previewAmount = ref(100);

    const roundingMethodLabel = computed(() => {
      const map = { round: '四捨五入', ceil: '無條件進位', floor: '無條件捨去' };
      return map[form.roundingMethod] || '無條件捨去';
    });

    const getRoundingMethodLabel = (method) => {
      const map = { round: '四捨五入', ceil: '無條件進位', floor: '無條件捨去' };
      return map[method] || '無條件捨去';
    };

    const getTierName = (tierCode) => {
      const tier = tiers.value.find(t => t.tierCode === tierCode);
      return tier ? tier.tierName : tierCode;
    };

    const hasActiveTierRates = (tierConfig) => {
      if (!tierConfig || typeof tierConfig !== 'object') return false;
      return Object.values(tierConfig).some(
        (rate) => rate !== null && rate !== undefined && rate !== ''
      );
    };

    const getTimeRangeStatusBadgeClass = (status) => {
      const classes = {
        draft: 'badge badge-secondary',
        scheduled: 'badge badge-info',
        active: 'badge badge-success',
        ended: 'badge badge-secondary',
        disabled: 'badge badge-warning'
      };
      return classes[status] || 'badge badge-secondary';
    };

    const getTimeRangeStatusText = (status) => {
      const texts = {
        draft: '草稿',
        scheduled: '已排程',
        active: '進行中',
        ended: '已結束',
        disabled: '已停用'
      };
      return texts[status] || status;
    };

    const applyRoundingPreview = (value) => {
      if (!isFinite(value) || isNaN(value)) return 0;
      switch (form.roundingMethod) {
        case 'ceil':  return Math.ceil(value);
        case 'round': return Math.round(value);
        case 'floor':
        default:      return Math.floor(value);
      }
    };

    const previewRawUnits = computed(() => {
      const base = (form.baseAmount > 0 && isFinite(form.baseAmount)) ? form.baseAmount : 1;
      const amount = (previewAmount.value > 0 && isFinite(previewAmount.value)) ? previewAmount.value : 0;
      return (amount / base).toFixed(2);
    });

    const previewUnits = computed(() => {
      const base = (form.baseAmount > 0 && isFinite(form.baseAmount)) ? form.baseAmount : 1;
      const amount = (previewAmount.value > 0 && isFinite(previewAmount.value)) ? previewAmount.value : 0;
      return applyRoundingPreview(amount / base);
    });

    const tierPreview = (tierCode) => {
      const rate = form.tierConfig[tierCode] || 0;
      const units = previewUnits.value;
      const points = applyRoundingPreview(units * (rate / 100));
      return { units, points: Math.max(0, points) };
    };

    const calcPreview = computed(() => {
      const base = (form.baseAmount > 0 && isFinite(form.baseAmount)) ? form.baseAmount : 1;
      const rawUnits = 180 / base;
      const units = applyRoundingPreview(rawUnits);
      const rawPoints = units * 2.5;
      const points = applyRoundingPreview(rawPoints);
      return {
        baseAmount: base,
        rawUnits: rawUnits.toFixed(2),
        units,
        rawPoints: rawPoints.toFixed(2),
        points
      };
    });

    // 監聽 showLogs 變化，當顯示時載入 log
    watch(showLogs, (newVal) => {
      if (newVal && rule.value && rule.value.id) {
        fetchLogs();
      }
    });

    onMounted(async () => {
      await Promise.all([fetchTiers(), fetchRule(), fetchActiveRule()]);
    });

    return {
      pageTitle,
      activeTab,
      showHelp,
      activeRuleLoading,
      activeTimeRangeRule,
      getTierName,
      hasActiveTierRates,
      getRoundingMethodLabel,
      getTimeRangeStatusBadgeClass,
      getTimeRangeStatusText,
      loading,
      submitting,
      toggling,
      error,
      rule,
      ruleStatus,
      tiers,
      form,
      errors,
      showLogs,
      logs,
      logsLoading,
      logsError,
      logsPagination,
      handleSubmit,
      handleEnable,
      handleDisable,
      handleReset,
      formatDateTime,
      fetchLogs,
      handleLogsPageChange,
      getActionBadgeClass,
      getActionText,
      formatChangeValue,
      getChangedByDisplay,
      roundingMethodLabel,
      applyRoundingPreview,
      calcPreview,
      previewAmount,
      previewRawUnits,
      previewUnits,
      tierPreview
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.time-range-active-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  color: #666;
  font-size: 14px;
}

.time-range-overrides-banner {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 1px solid #fcd34d;
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
  padding: 18px 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.1);
}

.overrides-banner-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 12px;
}

.overrides-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.overrides-pulse-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #f59e0b;
  box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.5);
  animation: overrides-pulse 1.8s ease-in-out infinite;
  flex-shrink: 0;
}

@keyframes overrides-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.5);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(245, 158, 11, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
  }
}

.overrides-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #92400e;
}

.overrides-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.btn-outline-warning {
  background: #fff;
  border: 1px solid #f59e0b;
  color: #b45309;
}

.btn-outline-warning:hover {
  background: #fffbeb;
}

.overrides-lead {
  margin: 0 0 14px;
  font-size: 14px;
  color: #78350f;
  line-height: 1.6;
}

.overrides-rule-card {
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 14px 16px;
}

.overrides-rule-name {
  font-size: 18px;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 6px;
}

.overrides-rule-period {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 14px;
  color: #b45309;
  margin-bottom: 14px;
}

.overrides-rule-period i {
  font-size: 16px;
}

.period-sep {
  color: #fcd34d;
}

.overrides-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
  padding-top: 14px;
  border-top: 1px dashed #fde68a;
}

.overrides-detail-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.overrides-detail-label {
  font-size: 12px;
  font-weight: 600;
  color: #92400e;
  letter-spacing: 0.02em;
}

.overrides-detail-empty {
  font-size: 13px;
  color: #6b7280;
}

.tier-rate-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tier-rate-badge {
  display: inline-block;
  padding: 4px 8px;
  background: #fff7ed;
  color: #c2410c;
  border: 1px solid #fed7aa;
  border-radius: 4px;
  font-size: 12px;
}

.overrides-calc-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
}

.calc-mode-tag {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.calc-mode-tag--normal {
  background: #dbeafe;
  color: #1d4ed8;
}

.calc-mode-tag--custom {
  background: #ffedd5;
  color: #c2410c;
}

/* 頁籤 */
.tab-bar {
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 24px;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border: none;
  background: none;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  border-radius: 4px 4px 0 0;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}

.tab-btn:hover {
  color: #4a90d9;
  background: #f5f8ff;
}

.tab-btn.active {
  color: #4a90d9;
  border-bottom-color: #4a90d9;
  background: #f5f8ff;
}

.quick-rule-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  background: linear-gradient(135deg, #eef5ff 0%, #f8fbff 100%);
  border: 1px solid #a9c9f5;
  border-left: 5px solid #2a67d5;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(42, 103, 213, 0.1);
  padding: 18px 20px;
  margin-bottom: 22px;
}

.quick-rule-summary-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: #1f55b5;
}

.quick-rule-summary-title i {
  font-size: 22px;
}

.quick-rule-summary-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  color: #334155;
  font-size: 14px;
  flex: 1;
}

.quick-rule-summary-items > span:not(.effective-mode-badge) {
  padding: 6px 10px;
  border: 1px solid #d3e2f7;
  border-radius: 6px;
  background: #fff;
  font-weight: 500;
}

.effective-mode-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 600;
}

.effective-mode-immediate {
  background: #dcfce7;
  color: #166534;
}

.effective-mode-delayed {
  background: #ffedd5;
  color: #9a3412;
}

/* 等級設定與預覽兩欄佈局 */
.tiers-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: start;
}

.sub-section-title {
  font-size: 15px;
  font-weight: 600;
  color: #444;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e8e8e8;
}

/* 右側預覽 */
.preview-amount-row {
  margin-bottom: 16px;
}

.preview-amount-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 6px;
}

.preview-amount-input {
  max-width: 200px;
}

.preview-settings-hint {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #888;
}

.preview-table-wrap {
  overflow-x: auto;
  margin-bottom: 12px;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.preview-table th {
  background: #f5f7fa;
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #e0e0e0;
}

.preview-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
}

.preview-tier-name {
  font-weight: 600;
}

.preview-units {
  color: #666;
}

.preview-points strong {
  font-size: 16px;
  color: #4a90d9;
}

.preview-formula-note {
  font-size: 12px;
  color: #777;
  background: #f8f9fa;
  border-radius: 6px;
  padding: 10px 14px;
  line-height: 1.8;
}

.preview-formula-note i {
  margin-right: 4px;
  color: #4a90d9;
}

@media (max-width: 768px) {
  .tiers-layout {
    grid-template-columns: 1fr;
  }
}

/* 計算公式預覽 */
.calc-preview-box {
  background: #f0f4ff;
  border: 1px solid #c8d8f8;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 24px;
}

.calc-preview-title {
  font-size: 13px;
  font-weight: 600;
  color: #4a6fa5;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.calc-preview-steps {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.calc-step {
  display: flex;
  align-items: center;
  gap: 10px;
}

.calc-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: #4a90d9;
  color: #fff;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.calc-step-text {
  font-size: 13px;
  color: #333;
}

.calc-arrow {
  margin: 0 4px;
  color: #4a90d9;
  font-weight: 600;
}

.calc-example {
  font-size: 12px;
  color: #555;
  background: #fff;
  border-radius: 6px;
  padding: 8px 12px;
  border: 1px solid #dce8fa;
  line-height: 1.7;
}

/* 金額基數 */
.base-amount-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 420px;
}

.base-amount-presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.base-amount-btn {
  padding: 6px 14px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
  color: #495057;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.base-amount-btn:hover {
  background: #f0f4ff;
  border-color: #4a90d9;
}

.base-amount-btn.active {
  background: #4a90d9;
  border-color: #4a90d9;
  color: #fff;
  font-weight: 600;
}

.base-amount-custom {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 180px;
}

.input-suffix {
  white-space: nowrap;
  color: #666;
  font-size: 14px;
}

/* ? 說明圖示按鈕 */
.help-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: 8px;
  border: 1px solid #aac8f0;
  border-radius: 50%;
  background: #e8f4fd;
  color: #1a73e8;
  font-size: 14px;
  cursor: pointer;
  vertical-align: middle;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  line-height: 1;
}

.help-icon-btn:hover,
.help-icon-btn.active {
  background: #1a73e8;
  border-color: #1a73e8;
  color: #fff;
}

/* 使用說明提示框 */
.setup-guide-alert {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 18px;
  background: #e8f4fd;
  border: 1px solid #b8daff;
  border-radius: 8px;
  margin-bottom: 20px;
  color: #004085;
}

.setup-guide-alert > i {
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
  color: #1a73e8;
}

.setup-guide-alert > div {
  flex: 1;
}

.setup-guide-alert strong {
  display: block;
  font-size: 14px;
  margin-bottom: 6px;
}

.setup-guide-alert ol {
  font-size: 13px;
  margin: 0;
  padding-left: 18px;
  color: #1a4a7a;
  line-height: 1.8;
}

.guide-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #6b9bd2;
  font-size: 18px;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.15s, color 0.15s;
}

.guide-close-btn:hover {
  background: #c8dff8;
  color: #004085;
}

/* 說明框展開/收合動畫 */
.guide-fade-enter-active,
.guide-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.guide-fade-enter-from,
.guide-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* 啟用狀態列 */
.rule-status-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  gap: 12px;
}

.status-active {
  background: #d4edda;
  border: 1px solid #c3e6cb;
}

.status-inactive {
  background: #fff3cd;
  border: 1px solid #ffeeba;
}

.status-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-active .status-dot {
  background: #28a745;
}

.status-inactive .status-dot {
  background: #856404;
}

.status-active .status-label {
  color: #155724;
  font-weight: 500;
  font-size: 14px;
}

.status-inactive .status-label {
  color: #856404;
  font-weight: 500;
  font-size: 14px;
}

.status-right {
  flex-shrink: 0;
}

.tier-config-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.tier-config-item {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #f9f9f9;
}

.tier-info {
  flex: 0 0 200px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tier-name {
  font-weight: 600;
  font-size: 16px;
}

.tier-code {
  font-size: 12px;
  color: #666;
  background: #e0e0e0;
  padding: 2px 6px;
  border-radius: 4px;
}

.tier-input {
  flex: 1;
}

.input-group {
  max-width: 200px;
}

.form-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #333;
}

.section-description {
  color: #666;
  margin-bottom: 20px;
  font-size: 14px;
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

.info-row {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
}

.info-label {
  font-weight: 500;
  color: #666;
  min-width: 120px;
}

.info-value {
  color: #333;
}

.logs-table-wrapper {
  margin-top: 16px;
}

.change-details {
  max-width: 400px;
}

.old-value,
.new-value {
  margin-bottom: 8px;
  font-size: 12px;
}

.old-value {
  color: #dc3545;
}

.new-value {
  color: #28a745;
}

.old-value pre,
.new-value pre {
  margin: 4px 0 0 0;
  padding: 4px 8px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 150px;
  overflow-y: auto;
}

.no-change {
  color: #999;
  font-style: italic;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

.page-info {
  color: #666;
  font-size: 14px;
}

@media (max-width: 768px) {
  .tier-config-item {
    flex-direction: column;
  }

  .tier-info {
    flex: 1;
  }
}
</style>
